import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Container, Modal, ModalBody, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { RefreshCw, X } from "react-feather";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import {
  getOrderRejections,
  getOrderRejection,
  updateOrderRejectionStatus,
} from "../../utils/orderRejectionApi";
import { successToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";

const CHANNEL_OPTIONS = [
  { value: "", label: "All Channels" },
  { value: "createOrder", label: "POS / App (createOrder)" },
  { value: "appOrder", label: "Pickup App (appOrder)" },
  { value: "deliverect", label: "Deliverect" },
  { value: "koinz", label: "Koinz" },
  { value: "blend", label: "Blend" },
  { value: "posLoyalty", label: "POS Loyalty" },
  { value: "screen", label: "Kiosk / Screen" },
  { value: "prepareOrder", label: "Prepare Order" },
];

const CHANNEL_LABEL = CHANNEL_OPTIONS.reduce(
  (acc, o) => (o.value ? { ...acc, [o.value]: o.label } : acc),
  {}
);

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "new", label: "New" },
  { value: "reviewed", label: "Reviewed" },
  { value: "ignored", label: "Ignored" },
  { value: "replayed", label: "Replayed" },
];

const STATUS_COLOR = {
  new: { bg: C.orangeLight, fg: C.orange },
  reviewed: { bg: C.greenLight, fg: C.green },
  ignored: { bg: C.bg, fg: C.textMed },
  replayed: { bg: C.blueLight, fg: C.blue },
};

const Pill = ({ status, label }) => {
  const c = STATUS_COLOR[status] || { bg: C.bg, fg: C.textMed };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.fg }} />
      {label || (status || "").replace(/_/g, " ").toUpperCase()}
    </span>
  );
};

// HTTP status chip — 5xx are server faults (coral), 4xx caller/validation faults
// (orange), 200 means "accepted transport but rejected in the body" (Deliverect).
const HttpChip = ({ status }) => {
  const palette =
    status >= 500
      ? { bg: C.coralLight, fg: C.coral }
      : status >= 400
      ? { bg: C.orangeLight, fg: C.orange }
      : { bg: C.blueLight, fg: C.blue };
  return (
    <span
      style={{
        padding: "3px 8px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: "monospace",
        background: palette.bg,
        color: palette.fg,
      }}
    >
      {status}
    </span>
  );
};

const ActionBtn = ({ label, onClick, disabled, variant = "primary" }) => {
  const palette =
    variant === "danger"
      ? { bg: C.coral, bgHover: C.coralDark, fg: C.white, border: C.coral }
      : { bg: C.white, bgHover: C.blueLight, fg: C.blue, border: C.blue };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "6px 12px",
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        background: palette.bg,
        color: palette.fg,
        border: `1.5px solid ${palette.border}`,
        transition: "all 0.15s",
        whiteSpace: "nowrap",
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = palette.bgHover;
      }}
      onMouseLeave={(e) => {
        if (!disabled) e.currentTarget.style.background = palette.bg;
      }}
    >
      {label}
    </button>
  );
};

const filterSelectStyle = {
  padding: "7px 12px",
  fontSize: 12,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  fontFamily: "inherit",
  outline: "none",
  color: C.text,
  background: C.white,
  cursor: "pointer",
  minWidth: 150,
};

const filterInputStyle = {
  ...filterSelectStyle,
  cursor: "text",
  minWidth: 180,
};

const OrderRejections = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector(
    (state) => state.merchant || {}
  );
  const selectedMerchant =
    userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [isLoading, setIsLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [rejections, setRejections] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [filters, setFilters] = useState({
    MerchantId: selectedMerchant || "",
    channel: "",
    status: "",
    search: "",
    dateFrom: "",
    dateTo: "",
    limit: 20,
  });

  const fetchRejections = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      const data = await getOrderRejections({
        page,
        limit: filters.limit,
        ...(filters.MerchantId && filters.MerchantId !== "all" && { MerchantId: filters.MerchantId }),
        ...(filters.channel && { channel: filters.channel }),
        ...(filters.status && { status: filters.status }),
        ...(filters.search && { search: filters.search }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: `${filters.dateTo}T23:59:59` }),
      });
      if (data) {
        setRejections(data.rows || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
      } else {
        setRejections([]);
        setTotal(0);
        setTotalPages(1);
      }
      setIsLoading(false);
    },
    [filters]
  );

  useEffect(() => {
    fetchRejections(currentPage);
  }, [fetchRejections, currentPage]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, MerchantId: selectedMerchant || "" }));
    setCurrentPage(1);
  }, [selectedMerchant]);

  // Debounced free-text search (externalId / error message).
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((prev) =>
        prev.search === searchText.trim() ? prev : { ...prev, search: searchText.trim() }
      );
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleStatusChange = async (rejection, status) => {
    setActingId(rejection.id);
    const updated = await updateOrderRejectionStatus(rejection.id, status);
    setActingId(null);
    if (updated) {
      successToast(`Rejection #${rejection.id} marked as ${status}`);
      fetchRejections(currentPage);
      // Merge — the status endpoint returns the row without Merchant/Branch/User includes.
      if (detail?.id === rejection.id) setDetail((prev) => ({ ...prev, ...updated }));
    }
  };

  const openDetail = async (rejection) => {
    setDetailLoading(true);
    setDetail(rejection); // show the light row immediately, payloads load in
    const full = await getOrderRejection(rejection.id);
    if (full) setDetail(full);
    setDetailLoading(false);
  };

  const customerName = (user) =>
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.phoneNo ||
    user?.email ||
    "—";

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).slice(
    Math.max(0, currentPage - 3),
    Math.min(totalPages, currentPage + 2)
  );

  return (
    <Fragment>
      <Breadcrumb title="Order Rejections" parent="Orders" />
      <Container fluid={true}>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <RCard>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <div
                onClick={() => fetchRejections(currentPage)}
                title="Refresh"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: `1.5px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: C.white,
                  flexShrink: 0,
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = C.coral;
                  e.currentTarget.style.background = C.coralLight;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = C.border;
                  e.currentTarget.style.background = C.white;
                }}
              >
                <RefreshCw size={15} color={C.textMed} />
              </div>

              <select
                value={filters.channel}
                onChange={(e) => setFilter("channel", e.target.value)}
                style={filterSelectStyle}
              >
                {CHANNEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              <select
                value={filters.status}
                onChange={(e) => setFilter("status", e.target.value)}
                style={filterSelectStyle}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              <input
                type="text"
                placeholder="Search external ID / reason..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={filterInputStyle}
              />

              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilter("dateFrom", e.target.value)}
                style={{ ...filterInputStyle, minWidth: 140 }}
                title="From date"
              />
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilter("dateTo", e.target.value)}
                style={{ ...filterInputStyle, minWidth: 140 }}
                title="To date"
              />

              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 12,
                  color: C.textLight,
                  background: C.bg,
                  padding: "4px 12px",
                  borderRadius: 20,
                }}
              >
                {total} rejections
              </span>
            </div>

            {isLoading ? (
              <RLoader />
            ) : (
              <div
                style={{
                  overflowX: "auto",
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                }}
              >
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>ID</th>
                      <th style={tbl.th}>Received</th>
                      <th style={tbl.th}>Channel</th>
                      <th style={tbl.th}>HTTP</th>
                      <th style={tbl.th}>Reason</th>
                      <th style={tbl.th}>External ID</th>
                      <th style={tbl.th}>Customer</th>
                      <th style={tbl.th}>Branch</th>
                      <th style={tbl.th}>Status</th>
                      <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rejections?.length > 0 ? (
                      rejections.map((rejection) => {
                        const busy = actingId === rejection.id;
                        const triaged = rejection.status !== "new";
                        return (
                          <tr key={rejection.id} className="rpt-row">
                            <td
                              style={{
                                ...tbl.td,
                                color: C.coral,
                                fontWeight: 700,
                                cursor: "pointer",
                              }}
                              onClick={() => openDetail(rejection)}
                              title="View rejection details"
                            >
                              #{rejection.id}
                            </td>
                            <td style={tbl.td}>{formatDate(rejection.createdAt)}</td>
                            <td style={{ ...tbl.td, fontWeight: 600 }}>
                              {CHANNEL_LABEL[rejection.channel] || rejection.channel}
                            </td>
                            <td style={tbl.td}>
                              <HttpChip status={rejection.httpStatus} />
                            </td>
                            <td style={{ ...tbl.td, maxWidth: 260, color: C.textMed }}>
                              {rejection.errorMessage || "—"}
                            </td>
                            <td style={{ ...tbl.td, fontFamily: "monospace", fontSize: 11, maxWidth: 160, wordBreak: "break-all" }}>
                              {rejection.externalId || "—"}
                            </td>
                            <td style={tbl.td}>{customerName(rejection.User)}</td>
                            <td style={tbl.td}>{rejection.Branch?.name || "—"}</td>
                            <td style={tbl.td}>
                              <Pill status={rejection.status} />
                            </td>
                            <td style={tbl.td}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  gap: 8,
                                }}
                              >
                                <ActionBtn
                                  label={busy ? "..." : "Reviewed"}
                                  disabled={triaged || busy}
                                  onClick={() => handleStatusChange(rejection, "reviewed")}
                                />
                                <ActionBtn
                                  label="Ignore"
                                  variant="danger"
                                  disabled={triaged || busy}
                                  onClick={() => handleStatusChange(rejection, "ignored")}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="10"
                          style={{
                            ...tbl.td,
                            textAlign: "center",
                            padding: 40,
                            color: C.textLight,
                          }}
                        >
                          No order rejections found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {rejections?.length > 0 && totalPages > 1 && (
              <Pagination className="d-flex justify-content-end mt-3 mb-0">
                <PaginationItem disabled={currentPage <= 1}>
                  <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                </PaginationItem>
                {pageNumbers.map((i) => (
                  <PaginationItem active={i === currentPage} key={i}>
                    <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem disabled={currentPage >= totalPages}>
                  <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                </PaginationItem>
              </Pagination>
            )}
          </RCard>
        </div>

        <RejectionDetailModal
          rejection={detail}
          loading={detailLoading}
          onClose={() => setDetail(null)}
          customerName={customerName}
          onStatusChange={handleStatusChange}
          actingId={actingId}
        />
      </Container>
    </Fragment>
  );
};

const DetailRow = ({ label, value, mono }) => (
  <div
    style={{
      display: "flex",
      gap: 12,
      padding: "9px 0",
      borderBottom: `1px solid ${C.borderLight}`,
    }}
  >
    <div style={{ flex: "0 0 150px", fontSize: 12, fontWeight: 600, color: C.textMed }}>
      {label}
    </div>
    <div
      style={{
        flex: 1,
        fontSize: 13,
        color: C.text,
        wordBreak: "break-word",
        fontFamily: mono ? "monospace" : "inherit",
      }}
    >
      {value === null || value === undefined || value === "" ? "—" : value}
    </div>
  </div>
);

const JsonBlock = ({ data }) => (
  <pre
    style={{
      margin: 0,
      padding: 12,
      background: C.bg,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      fontSize: 11,
      lineHeight: 1.5,
      color: C.text,
      maxHeight: 240,
      overflow: "auto",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}
  >
    {(() => {
      try {
        return JSON.stringify(typeof data === "string" ? JSON.parse(data) : data, null, 2);
      } catch (e) {
        return String(data);
      }
    })()}
  </pre>
);

const RejectionDetailModal = ({ rejection, loading, onClose, customerName, onStatusChange, actingId }) => {
  if (!rejection) return null;
  const busy = actingId === rejection.id;
  const triaged = rejection.status !== "new";
  return (
    <Modal isOpen={!!rejection} toggle={onClose} centered size="lg">
      <style>{reportingGlobalCSS}</style>
      <ModalBody style={{ padding: 0 }} className="rpt-wrap">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 22px",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
              Rejection #{rejection.id}
            </span>
            <Pill status={rejection.status} />
            <HttpChip status={rejection.httpStatus} />
          </div>
          <div
            onClick={onClose}
            style={{ cursor: "pointer", color: C.textMed, display: "flex" }}
            title="Close"
          >
            <X size={20} />
          </div>
        </div>

        <div style={{ padding: "12px 22px 22px" }}>
          <SectionTitle title="Rejection" />
          <DetailRow
            label="Channel"
            value={CHANNEL_LABEL[rejection.channel] || rejection.channel}
          />
          <DetailRow label="Received" value={formatDate(rejection.createdAt)} />
          <DetailRow
            label="Error"
            value={<span style={{ color: C.coral, fontWeight: 600 }}>{rejection.errorMessage || "—"}</span>}
          />
          <DetailRow label="Error code" value={rejection.errorCode} mono />

          <SectionTitle title="Correlation" />
          <DetailRow label="External ID" value={rejection.externalId} mono />
          <DetailRow label="Customer" value={customerName(rejection.User)} />
          <DetailRow label="Merchant" value={rejection.Merchant?.name} />
          <DetailRow label="Branch" value={rejection.Branch?.name} />

          <SectionTitle title="Triage" />
          <DetailRow
            label="Reviewed by"
            value={rejection.reviewedByUserId ? `User #${rejection.reviewedByUserId}` : null}
          />
          <DetailRow label="Reviewed at" value={rejection.reviewedAt ? formatDate(rejection.reviewedAt) : null} />
          <DetailRow label="Notes" value={rejection.notes} />
          {!triaged && (
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <ActionBtn
                label={busy ? "..." : "Mark reviewed"}
                disabled={busy}
                onClick={() => onStatusChange(rejection, "reviewed")}
              />
              <ActionBtn
                label="Ignore"
                variant="danger"
                disabled={busy}
                onClick={() => onStatusChange(rejection, "ignored")}
              />
            </div>
          )}

          {loading ? (
            <RLoader />
          ) : (
            <>
              {rejection.requestPayload ? (
                <>
                  <SectionTitle title="Request payload (as received)" />
                  <JsonBlock data={rejection.requestPayload} />
                </>
              ) : null}

              {rejection.responseBody ? (
                <>
                  <SectionTitle title="Response sent to caller" />
                  <JsonBlock data={rejection.responseBody} />
                </>
              ) : null}

              {rejection.requestMeta ? (
                <>
                  <SectionTitle title="Request meta" />
                  <JsonBlock data={rejection.requestMeta} />
                </>
              ) : null}
            </>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

const SectionTitle = ({ title }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: C.coral,
      marginTop: 18,
      marginBottom: 4,
    }}
  >
    {title}
  </div>
);

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "—";
  return (
    date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }) +
    ", " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
  );
};

export default OrderRejections;
